/**
 * @file     SubmitOfferAttemptModal.tsx
 * @purpose  Make an offer: one listing sheet with offer price, possession
 *           date, subjects, a Request a call switch (on by default) and first
 *           name, last name, email and mobile phone, all required, above the
 *           one consent line. Nothing reaches the seller: /api/offer-attempts
 *           files the details (price on its own column, the rest in the note)
 *           and forwards them to the CRM so the offer can be prepared.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { analyticsEvents } from '@/core/constants/analytics';
import { gatedSecureFetch } from '@/services/phone-gate/gatedFetch';
import { useConsentChoices } from '@/components/lead-capture/ConsentChoices';
import { ContactFields } from '@/components/lead-capture/ContactFields';
import { RealtorQuestion } from '@/components/lead-capture/RealtorQuestion';
import { gateColors } from '@/components/gate/GateSheet';
import { ListingSheet, SheetChip, SheetFooter, SheetLabel, SheetSent, SHEET_ICONS } from '@/components/lead-capture/ListingSheet';
import { contactPayload, validateContact, type ContactErrors, type ContactField, type ContactValues } from '@/services/leads/contactFields';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { formatNumericDate } from '@/utils/dateFormat';
import { accountContact } from './ScheduleShowingModal';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fired once the attempt is recorded (new or already-existing), with the REALTOR® answer. */
  onSubmitted?: (workingWithRealtor: boolean) => void;
  listingKey: string;
  listingPrice: number | null;
  listingAddress?: string | null;
  listingSlug?: string | null;
  listingPlace?: string | null;
  listingThumb?: string | null;
  onSeeSimilar?: () => void;
}

const SUBJECTS = [
  { id: 'financing', key: 'offerSheet.subject.financing', label: 'Financing' },
  { id: 'inspection', key: 'offerSheet.subject.inspection', label: 'Inspection' },
  { id: 'strata', key: 'offerSheet.subject.strata', label: 'Strata documents' },
] as const;

/** The route's accepted range. */
const MIN_OFFER = 50_000;
const MAX_OFFER = 50_000_000;

const digits = (raw: string) => raw.replace(/\D/g, '');
const groupDigits = (raw: string) => {
  const d = digits(raw).slice(0, 9);
  return d ? Number(d).toLocaleString('en-CA') : '';
};

/** The note the CRM receives: possession date, subjects and whether to call. */
export function offerNote({ possession, subjects, requestCall }: { possession: string; subjects: string[]; requestCall: boolean }): string {
  return [
    possession ? `Possession date: ${possession}` : null,
    `Subjects: ${subjects.length ? subjects.join(', ') : 'none'}`,
    `Request a call: ${requestCall ? 'yes' : 'no'}`,
  ]
    .filter(Boolean)
    .join('\n');
}

export function SubmitOfferAttemptModal({
  open,
  onClose,
  onSubmitted,
  listingKey,
  listingPrice,
  listingAddress,
  listingSlug,
  listingPlace,
  listingThumb,
  onSeeSimilar,
}: Props) {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { trackEvent } = useAnalytics();
  const [price, setPrice] = useState('');
  const [priceError, setPriceError] = useState<string | null>(null);
  const [possession, setPossession] = useState('');
  const [subjects, setSubjects] = useState<string[]>(['financing', 'inspection']);
  const [requestCall, setRequestCall] = useState(true);
  const [contact, setContact] = useState<ContactValues>({ firstName: '', lastName: '', email: '', phone: '' });
  const [errors, setErrors] = useState<ContactErrors>({});
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const consent = useConsentChoices();
  const resetConsent = consent.reset;
  const [realtor, setRealtor] = useState<boolean | null>(null);
  const [realtorError, setRealtorError] = useState(false);
  const [sent, setSent] = useState<{ existed: boolean; call: boolean } | null>(null);
  const openedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;
    setPrice(listingPrice ? Math.round(listingPrice).toLocaleString('en-CA') : '');
    setPriceError(null);
    setPossession('');
    setSubjects(['financing', 'inspection']);
    setRequestCall(true);
    setContact(accountContact(isAuthenticated ? user : null));
    setErrors({});
    setFailed(false);
    resetConsent();
    setRealtor(null);
    setRealtorError(false);
    setSent(null);
  }, [open, listingPrice, isAuthenticated, user, resetConsent]);

  const close = () => {
    if (pending) return;
    onClose();
  };

  const submit = async () => {
    if (pending) return;
    const amount = Number(digits(price));
    const amountOk = amount >= MIN_OFFER && amount <= MAX_OFFER;
    setPriceError(amountOk ? null : t('offerSheet.priceError', { defaultValue: 'Enter an offer price.' }));
    const found = validateContact(contact, t);
    setErrors(found);
    setRealtorError(realtor === null);
    if (!amountOk || Object.keys(found).length > 0 || realtor === null) return;

    const person = contactPayload(contact);
    const subjectLabels = SUBJECTS.filter((s) => subjects.includes(s.id)).map((s) => s.label);
    setFailed(false);
    setPending(true);
    try {
      const res = await gatedSecureFetch('/api/offer-attempts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listingKey,
          listingSlug,
          listingPrice,
          listingAddress,
          offerAmount: amount,
          message: offerNote({ possession, subjects: subjectLabels, requestCall }),
          firstName: person.firstName,
          lastName: person.lastName,
          email: person.email,
          phone: person.phone,
          workingWithRealtor: realtor,
          ...consent.payload(person.phone),
        }),
      });
      if (!res.ok) throw new Error(`offer ${res.status}`);
      const body = (await res.json().catch(() => null)) as { alreadyExisted?: boolean } | null;
      setSent({ existed: Boolean(body?.alreadyExisted), call: requestCall });
      onSubmitted?.(realtor);
      trackEvent({
        eventName: analyticsEvents.propertyOfferAttemptStepChanged,
        eventType: 'funnel',
        category: 'offer_attempt',
        label: 'success',
        metadata: { listing_key: listingKey, step: 'success' },
      });
      trackEvent({
        eventName: analyticsEvents.propertyOfferAttemptSubmitted,
        eventType: 'conversion',
        category: 'offer_attempt',
        label: 'submitted',
        metadata: {
          listing_key: listingKey,
          list_price: listingPrice,
          offer_amount: amount,
          pct_of_list: listingPrice ? Number(((amount / listingPrice) * 100).toFixed(1)) : null,
          authenticated: isAuthenticated,
          has_message: true,
          request_call: requestCall,
          subject_count: subjectLabels.length,
          working_with_realtor: realtor,
        },
      });
    } catch (err) {
      setFailed(true);
      trackEvent({
        eventName: analyticsEvents.propertyOfferAttemptFailed,
        eventType: 'funnel',
        category: 'offer_attempt',
        label: 'submit_failed',
        metadata: { listing_key: listingKey, view: 'sheet', error: err instanceof Error ? err.message : 'failed' },
      });
    } finally {
      setPending(false);
    }
  };

  const sheetListing = { title: listingAddress ?? '', price: listingPrice, place: listingPlace, thumbUrl: listingThumb };
  const title = sent
    ? t('offerSheet.sent', { defaultValue: 'Offer details sent' })
    : t('offerSheet.title', { defaultValue: 'Make an offer' });
  const tomorrow = formatNumericDate(new Date(Date.now() + 86_400_000));

  return (
    <ListingSheet
      open={open}
      onClose={close}
      listing={sheetListing}
      title={title}
      titleHidden={Boolean(sent)}
      stepKey={sent ? 'sent' : 'form'}
      testId="offer-sheet"
    >
      {sent ? (
        <SheetSent
          title={title}
          line={
            sent.call
              ? t('offerSheet.sentCall', { defaultValue: 'You asked for a call to prepare the offer.' })
              : t('offerSheet.sentEmail', { defaultValue: 'Ray Rasouli will reply by email.' })
          }
          primary={{ label: t('listingSheet.backToListing', { defaultValue: 'Back to listing' }), icon: SHEET_ICONS.back, onClick: onClose }}
          secondary={
            onSeeSimilar
              ? {
                  label: t('listingActions.label.seeSimilar', { defaultValue: 'See similar homes' }),
                  onClick: () => {
                    onClose();
                    onSeeSimilar();
                  },
                }
              : null
          }
        />
      ) : (
        <>
          <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField
              id="offer-price"
              label={t('offerSheet.price', { defaultValue: 'Offer price' })}
              value={price}
              onChange={(e) => {
                setPrice(groupDigits(e.target.value));
                setPriceError(null);
              }}
              error={Boolean(priceError)}
              helperText={priceError ?? undefined}
              required
              disabled={pending}
              slotProps={{
                htmlInput: { inputMode: 'numeric' },
                input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
              }}
            />
            <TextField
              id="offer-possession"
              type="date"
              label={t('offerSheet.possession', { defaultValue: 'Possession date' })}
              value={possession}
              onChange={(e) => setPossession(e.target.value)}
              disabled={pending}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: tomorrow } }}
            />
          </Box>
          <Box role="group" aria-labelledby="offer-subjects-label" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SheetLabel id="offer-subjects-label">{t('offerSheet.subjects', { defaultValue: 'Subjects' })}</SheetLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {SUBJECTS.map((subject) => (
                <SheetChip
                  key={subject.id}
                  role="checkbox"
                  withIcon
                  pressed={subjects.includes(subject.id)}
                  onToggle={() =>
                    setSubjects((prev) => (prev.includes(subject.id) ? prev.filter((id) => id !== subject.id) : [...prev, subject.id]))
                  }
                >
                  {t(subject.key, { defaultValue: subject.label })}
                </SheetChip>
              ))}
            </Box>
          </Box>
          <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 3, minHeight: 44, cursor: 'pointer' }}>
            <Switch
              checked={requestCall}
              onChange={(e) => setRequestCall(e.target.checked)}
              disabled={pending}
              slotProps={{ input: { role: 'switch' } }}
              sx={{ ml: -2.5 }}
            />
            <Box component="span" sx={{ fontSize: 15, fontWeight: fontWeightTokens.medium, color: gateColors.text }}>
              {t('offerSheet.requestCall', { defaultValue: 'Request a call' })}
            </Box>
          </Box>
          <ContactFields
            values={contact}
            errors={errors}
            onChange={(field: ContactField, value: string) => {
              setContact((prev) => ({ ...prev, [field]: value }));
              setErrors((prev) => ({ ...prev, [field]: undefined }));
            }}
            disabled={pending}
            idPrefix="offer"
          />
          <RealtorQuestion
            value={realtor}
            onChange={setRealtor}
            yesLine={t('forms.realtor.yesOffer', { defaultValue: 'The REALTOR® you work with can also send this offer for you.' })}
            error={realtorError}
            disabled={pending}
            idPrefix="offer"
          />
          <SheetFooter
            next={t('offerSheet.next', { defaultValue: 'Nothing goes to the seller until you sign an offer.' })}
            cta={t('offerSheet.submit', { defaultValue: 'Start my offer' })}
            pending={pending}
            error={failed}
            onSubmit={() => void submit()}
            consent={{ action: t('forms.consent.continuing', { defaultValue: 'continuing' }) }}
            consentChoices={{ values: consent.values, onChange: consent.setValues, phone: true }}
          />
        </>
      )}
    </ListingSheet>
  );
}
